import React, { useEffect } from 'react';
import { createContext, useContext, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Spinner } from 'components/Spinner';
import { useMsal } from '@azure/msal-react';
import { useAppSelector } from 'store';
import { userSelector } from 'api/slices/userSlice';
import { UserInfoData } from 'api/types/types';
import { getValidToken } from './Authenticator';
import { getRolesFromToken } from 'utils/helpers';

export type UserRoles =
  | 'travel-request-view'
  | 'travel-request-edit'
  | 'travel-request-admin';
interface AuthContextType {
  isAuthenticated: boolean | undefined;
  logout: () => void;
  hasAccess: () => boolean | undefined;
  user: UserInfoData | null;
  hasRoles: (role: UserRoles) => boolean | undefined;
  userRoles: UserRoles[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider = (): JSX.Element => {
  const { instance } = useMsal();
  const { user } = useAppSelector(userSelector);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>();
  const [userRoles, setUserRoles] = useState<Array<UserRoles>>([]);

  const logout = (): void => {
    instance.logout();
  };

  const getRoles = async (): Promise<void> => {
    const token = await getValidToken();
    if (token) {
      setUserRoles(getRolesFromToken(token));
    }
  };

  useEffect(() => {
    if (instance.getAllAccounts().length > 0) {
      getRoles();
      setIsAuthenticated(true);
    }
  }, [instance]);

  const hasRoles = (role: UserRoles): boolean => {
    if (!role || userRoles === undefined || userRoles.length === 0)
      return false;
    return userRoles.includes(role);
  };

  const hasAccess = (): boolean | undefined => {
    return user?.hasAccess;
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, logout, hasAccess, user, hasRoles, userRoles }}
    >
      {isAuthenticated === undefined && user === null ? (
        <Spinner />
      ) : (
        <Outlet />
      )}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
